import { Box, Stack, Alert } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import { pageGestalt } from './usePageGestalt'
import { noChartsForKpiNote, MESSAGES } from '~/components/Charts/chrome/chartChrome'
import { ComparisonChartTile } from './ComparisonChartTile'
import { SummaryStripTile } from './SummaryStripTile'
import { MetricDriverBarsTile } from './MetricDriverBarsTile'
import { DynamicsPanelsTile } from './DynamicsPanelsTile'
import { MultiTimeframeOverlayTile } from './MultiTimeframeOverlayTile'
import type { ReviewPageState } from './useReviewPageState'
import type { ComparisonState } from './useComparisonState'

interface Props {
  charts: UseQueryResult<ChartRegistryRow[], Error>
  state: ReviewPageState
  compare: ComparisonState
  pageSlug?: string
}

// Renders a page's tiles FROM chart_registry (via the pure pageGestalt selector), dispatching by
// chart_type. Replaces the per-page hardcoded <ComparisonChartTile> JSX. Loud-fails on unknown
// chart_type (Alert listing the bad types) and on a charts-query error.
export function PageTiles({ charts, state, compare, pageSlug }: Props) {
  const gestalt = pageGestalt(charts)

  if (gestalt.status === 'loading') return null // scaffold shows the spinner pre-timeframeReady
  if (gestalt.status === 'error') {
    return <Alert severity="error">{MESSAGES.chartsLoadFailed}</Alert>
  }
  if (gestalt.status === 'unknown') {
    return (
      <Alert severity="error">{MESSAGES.unsupportedChartType(gestalt.unknown.join(', '))}</Alert>
    )
  }

  // Empty registry for a SETTLED kpi (e.g. a kpi_root carried across tabs that this page
  // doesn't offer) → loud note. encodedKpi === '' is the legitimate cascade-loading
  // transient (useReviewPageState's bare-default note) — render nothing there.
  if (gestalt.tiles.length === 0) {
    if (state.kpiCascade.encodedKpi === '') return null
    return <Alert severity="warning">{noChartsForKpiNote(state.kpiCascade.encodedKpi)}</Alert>
  }

  return (
    <Stack spacing={2}>
      {gestalt.tiles.map((t) => (
        <Box key={t.key} data-testid="page-tile">
          {t.chart_type === 'summary_bar_strip' ? (
            <SummaryStripTile state={state} suppressBaseline={compare.mode !== 'off'} />
          ) : t.chart_type === 'metric_driver_bars' ? (
            <MetricDriverBarsTile state={state} charts={charts} />
          ) : t.chart_type === 'dynamics_panels' ? (
            <DynamicsPanelsTile state={state} charts={charts} compare={compare} />
          ) : t.chart_type === 'multi_timeframe_overlay' ? (
            <MultiTimeframeOverlayTile state={state} charts={charts} />
          ) : (
            <ComparisonChartTile
              kind={t.chart_type}
              charts={charts}
              state={state}
              compare={compare}
              pageSlug={pageSlug}
            />
          )}
        </Box>
      ))}
    </Stack>
  )
}
